import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useBlindReviewLogs } from '@/api-hooks/useBlindReview';
import { useUser } from '@/api-hooks/useUser';
import Button from '@/components/v4/Button';
import { ModalXIconButton } from '@/components/v4/Modal/ModalPaper';
import { Review } from '@/types/review';

import { ArrowDownIcon, DocumentSignatureIcon, ReadOnlyInfoCard } from '../shared';

/** 쿠팡이츠 블라인드 신청 내역 (pending 클릭 시) */
export const CpeatsInfoContent = ({
  isOpen,
  review,
  onClose,
}: {
  isOpen: boolean;
  review: Review;
  onClose: () => void;
}) => {
  const { data: blindReviewLogsResponse, isLoading } = useBlindReviewLogs(
    { review_id: review.review_id },
    { enabled: isOpen },
  );
  const { data: user } = useUser();

  const blindLog = blindReviewLogsResponse?.data?.[0];
  const formattedRequestedAt = blindLog?.created_at
    ? formatDate(blindLog.created_at)
    : 'YY.MM.DD 00:00';

  const businessNumber = user?.business_number || '-';
  const storeId = review.store_id || '-';
  const orderNumber = review.platform_order_id || '-';
  const orderDate = review.created_at ? dayjs(review.created_at).format('YYYY년 MM월 DD일') : '-';

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [hasScrollableContent, setHasScrollableContent] = useState(false);

  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    setHasScrollableContent(scrollHeight > clientHeight);
    setIsScrolledToBottom(
      scrollHeight <= clientHeight || scrollHeight - scrollTop - clientHeight < 10,
    );
  }, []);

  useEffect(() => {
    if (isOpen) {
      setIsScrolledToBottom(false);
      setTimeout(checkScrollPosition, 100);
    }
  }, [isOpen, checkScrollPosition]);

  return (
    <div className="flex flex-col relative h-[80vh] max-h-[700px] md:h-[700px]">
      <div className="flex items-start md:items-center justify-between px-5 py-2.5 mt-2.5 shrink-0">
        <div className="flex flex-col md:flex-row md:items-center gap-0.5 md:gap-2">
          <p className="text-black font-semibold text-lg">리뷰 블라인드 신청 내역</p>
          <span className="text-cds-coolgray-50 text-xs font-normal tracking-[-0.132px]">
            {formattedRequestedAt} 신청됨
          </span>
        </div>
        <ModalXIconButton onClick={onClose} dataGtmId="blind-request-info-modal-close" />
      </div>

      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto px-5 md:px-20 pb-40 pt-1 md:pt-2.5"
        onScroll={checkScrollPosition}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-cds-orange-55 border-t-transparent" />
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center pb-5">
              <DocumentSignatureIcon />
              <p className="text-cds-black-99 font-semibold text-lg leading-[150%] mt-2 text-center">
                아래 정보를 입력해서
              </p>
              <p className="text-cds-black-99 font-semibold text-lg leading-[150%] text-center">
                자동으로 신청했어요
              </p>
              <p className="text-cds-coolgray-60 text-sm mt-1 text-center">
                승인되면 영업일 최대 2일 내 처리돼요
              </p>
            </div>

            <div className="flex flex-col gap-2.5">
              <ReadOnlyInfoCard label="사업자번호" value={businessNumber} />
              <ReadOnlyInfoCard label="스토어 ID" value={storeId} />
              <ReadOnlyInfoCard label="주문번호" value={orderNumber} />
              <ReadOnlyInfoCard label="주문 일자" value={orderDate} />
              <ReadOnlyInfoCard label="리뷰 내용" value={review.content || '-'} multiline />
            </div>
            <div ref={bottomRef} />
          </>
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 mx-5 md:mx-5">
        <div
          className="px-5 md:px-20"
          style={{
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 90%)',
          }}
        >
          {hasScrollableContent && !isScrolledToBottom ? (
            <Button
              variant="contained"
              color="orange"
              className="w-full rounded-xl flex items-center justify-center gap-2"
              height={40}
              onClick={() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' })}
              data-gtm-id="blind-info-cpeats-end"
            >
              끝까지 확인하기 <ArrowDownIcon />
            </Button>
          ) : (
            <Button
              variant="tonal"
              color="coolgray"
              className="w-full rounded-xl"
              height={40}
              onClick={onClose}
            >
              닫기
            </Button>
          )}
        </div>
        <div className="h-6 bg-white" />
      </div>
    </div>
  );
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const year = String(date.getFullYear()).slice(-2);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}.${month}.${day} ${hours}:${minutes}`;
};
